import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Trash2, KeyRound, UserPlus, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";
import { Avatar } from "./Avatar";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "./ui/alert-dialog";
import { formatDateLong } from "../lib/padel";
import { AdminPlayerActions } from "./AdminPlayerActions";

const run = async (fn, ok, after) => {
  try { await fn(); toast.success(ok); after?.(); }
  catch (e) { toast.error(e?.response?.data?.detail || "Operazione non riuscita"); }
};
const card = "rounded-2xl bg-[#121b2d] border border-[#1e2c4a] p-3";
const selCls = "bg-[#0b1420] border-[#1e2c4a] text-slate-100 h-9 rounded-lg";
const popCls = "bg-[#121b2d] border-[#1e2c4a] text-slate-100 max-h-64";

const Confirm = ({ title, desc, onConfirm, testid, children }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>
    <AlertDialogContent className="bg-[#121b2d] border-[#1e2c4a] text-slate-100">
      <AlertDialogHeader>
        <AlertDialogTitle>{title}</AlertDialogTitle>
        <AlertDialogDescription className="text-slate-400">{desc}</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel className="bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800 hover:text-white">Annulla</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} data-testid={`${testid}-confirm`} className="bg-[#ff4d4d] hover:bg-[#e04343] text-white">Conferma</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

const DelBtn = ({ testid }) => (
  <button data-testid={testid} className="p-2 rounded-lg text-slate-400 hover:text-[#ff4d4d] hover:bg-[#ff4d4d]/10"><Trash2 className="w-4 h-4" /></button>
);

const UserRow = ({ u, reload }) => {
  const [pw, setPw] = useState("");
  const isAdm = u.role === "admin";
  return (
    <div className={card} data-testid={`admin-user-${u.username_lower}`}>
      <div className="flex items-center gap-3">
        <Avatar name={u.username} photoPath={u.photo_path} size={34} />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-slate-100 truncate flex items-center gap-1.5">{u.username}{isAdm && <ShieldCheck className="w-4 h-4 text-[#ccff00]" />}</p>
          <p className="text-xs text-slate-500">Iscritto il {(u.created_at || "").slice(0, 10)}</p>
        </div>
        {!isAdm && (
          <Confirm title={`Eliminare ${u.username}?`} desc="Account, voti e pagelle date verranno eliminati. Le partite giocate restano nello storico." testid={`admin-delete-user-${u.username_lower}`}
            onConfirm={() => run(() => api.adminDeleteUser(u.id), "Iscritto eliminato", reload)}>
            <span><DelBtn testid={`admin-delete-user-${u.username_lower}`} /></span>
          </Confirm>
        )}
      </div>
      {!isAdm && (
      <div className="flex gap-2 mt-2">
        <Input value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Nuova password" data-testid={`admin-pw-input-${u.username_lower}`} className="bg-[#0b1420] border-[#1e2c4a] text-slate-100 h-9" />
        <Button disabled={pw.length < 4} data-testid={`admin-pw-btn-${u.username_lower}`} onClick={() => run(() => api.adminResetPassword(u.id, pw), `Password di ${u.username} reimpostata`, () => setPw(""))}
          className="bg-[#1e2c4a] hover:bg-[#263a5f] text-[#ccff00] h-9 rounded-lg shrink-0"><KeyRound className="w-4 h-4 mr-1" /> Reimposta</Button>
      </div>
      )}
    </div>
  );
};

const MatchRow = ({ m, users, reload }) => {
  const [add, setAdd] = useState("");
  const free = users.filter((u) => u.role !== "admin" && !m.players.some((p) => p.name.toLowerCase() === u.username_lower));
  return (
    <div className={card} data-testid={`admin-match-${m.id}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-slate-100 truncate">{m.venue}</p>
          <p className="text-xs text-slate-400">{formatDateLong(m.date)} · {m.time} · {m.players.length}/{m.total_slots} · org. {m.organizer}</p>
          {m.result && <p className="text-xs text-[#ccff00] mt-0.5">Risultato: {m.result.sets.map((s) => `${s.a}-${s.b}`).join(" ")}</p>}
        </div>
        {m.result && (
          <Confirm title="Cancellare il risultato?" desc="Verranno eliminate anche le pagelle di questa partita." testid={`admin-delete-result-${m.id}`}
            onConfirm={() => run(() => api.deleteResult(m.id), "Risultato cancellato", reload)}>
            <Button size="sm" variant="outline" data-testid={`admin-delete-result-${m.id}`} className="bg-transparent border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white h-8 text-xs">Togli risultato</Button>
          </Confirm>
        )}
        <Confirm title="Eliminare la partita?" desc="La partita, il risultato e le pagelle verranno eliminati." testid={`admin-delete-match-${m.id}`}
          onConfirm={() => run(() => api.deleteMatch(m.id), "Partita eliminata", reload)}>
          <span><DelBtn testid={`admin-delete-match-${m.id}`} /></span>
        </Confirm>
      </div>
      <div className="flex gap-2 mt-2">
        <Select value={add} onValueChange={setAdd}>
          <SelectTrigger data-testid={`admin-add-select-${m.id}`} className={selCls}><SelectValue placeholder="Aggiungi giocatore" /></SelectTrigger>
          <SelectContent className={popCls}>
            {free.map((u) => <SelectItem key={u.id} value={u.username} data-testid={`admin-add-option-${m.id}-${u.username_lower}`}>{u.username}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button disabled={!add} data-testid={`admin-add-btn-${m.id}`} onClick={() => run(() => api.adminAddPlayer(m.id, add), `${add} aggiunto`, () => { setAdd(""); reload(); })}
          className="bg-[#ccff00] hover:bg-[#aeea00] text-[#090d16] h-9 rounded-lg shrink-0"><UserPlus className="w-4 h-4" /></Button>
      </div>
    </div>
  );
};

const VotesList = () => {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.adminVotes().then(setRows).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!rows.length) return <p className="text-sm text-slate-500" data-testid="admin-votes-empty">Nessun voto quest'anno.</p>;
  return rows.map((v) => (
    <div key={`${v.category}-${v.voter_id}`} className={`${card} flex items-center gap-2`} data-testid={`admin-vote-${v.category}-${v.voter.toLowerCase()}`}>
      <p className="flex-1 text-sm text-slate-200"><b>{v.voter}</b> → {v.candidate} <span className="text-xs text-slate-500">({v.category}{v.secret ? ", segreto" : ""})</span></p>
      <Confirm title="Eliminare questo voto?" desc={`Voto di ${v.voter} per ${v.candidate}.`} testid={`admin-delete-vote-${v.category}-${v.voter.toLowerCase()}`}
        onConfirm={() => run(() => api.adminDeleteVote(v.year, v.category, v.voter_id), "Voto eliminato", load)}>
        <span><DelBtn testid={`admin-delete-vote-${v.category}-${v.voter.toLowerCase()}`} /></span>
      </Confirm>
    </div>
  ));
};

const RatingsList = () => {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.adminRatings().then(setRows).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!rows.length) return <p className="text-sm text-slate-500" data-testid="admin-ratings-empty">Nessuna pagella.</p>;
  return rows.map((r) => {
    const id = `${r.match_id}-${r.rater_key}-${r.target_key}`;
    return (
      <div key={id} className={`${card} flex items-center gap-2`} data-testid={`admin-rating-${id}`}>
        <p className="flex-1 text-sm text-slate-200"><b>{r.rater}</b> → {r.target}: <span className="text-[#ccff00] font-bold">{String(r.score).replace(".", ",")}</span> <span className="text-xs text-slate-500">{r.match_label}</span></p>
        <Confirm title="Eliminare questo voto in pagella?" desc={`${r.rater} → ${r.target}`} testid={`admin-delete-rating-${id}`}
          onConfirm={() => run(() => api.adminDeleteRating(r.match_id, r.rater_key, r.target_key), "Voto eliminato", load)}>
          <span><DelBtn testid={`admin-delete-rating-${id}`} /></span>
        </Confirm>
      </div>
    );
  });
};

const KIND_LABEL = { comment: "Commento partita", reply: "Risposta partita", campaign: "Campagna elettorale" };
const deleteReported = (r) => (r.kind === "campaign" ? api.campaignDelete(r.target_id)
  : r.kind === "reply" ? api.deleteReply(r.match_id, r.comment_id, r.target_id)
  : api.deleteComment(r.match_id, r.target_id));

const ReportsList = () => {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.adminReports().then(setRows).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!rows.length) return <p className="text-sm text-slate-500" data-testid="admin-reports-empty">Nessuna segnalazione.</p>;
  return rows.map((r) => (
    <div key={r.id} className={card} data-testid={`admin-report-${r.id}`}>
      <p className="text-xs text-slate-500">{KIND_LABEL[r.kind]} · segnalato da <b className="text-slate-300">{r.reporter}</b> · {(r.created_at || "").slice(0, 10)}</p>
      <p className="text-sm text-slate-200 mt-1"><b className="text-[#ccff00]">{r.author}</b>: {r.text}</p>
      <div className="flex gap-2 mt-2">
        <Button size="sm" data-testid={`admin-report-delete-${r.id}`} className="bg-[#ff4d4d] hover:bg-[#e04343] text-white h-8 text-xs rounded-lg"
          onClick={() => run(async () => { await deleteReported(r); await api.adminDismissReport(r.id); }, "Messaggio eliminato", load)}>
          <Trash2 className="w-3.5 h-3.5 mr-1" /> Elimina messaggio
        </Button>
        <Button size="sm" variant="outline" data-testid={`admin-report-dismiss-${r.id}`} className="bg-transparent border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white h-8 text-xs rounded-lg"
          onClick={() => run(() => api.adminDismissReport(r.id), "Segnalazione ignorata", load)}>
          Ignora
        </Button>
      </div>
    </div>
  ));
};

const PlayersList = ({ onChanged }) => {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const load = useCallback(() => api.adminPlayers().then(setRows).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const changed = () => { load(); onChanged(); };
  return (
    <>
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca giocatore..." data-testid="admin-player-search" className="bg-[#121b2d] border-[#1e2c4a] text-slate-100 h-10 mb-1" />
      {rows.filter((r) => r.key.includes(q.trim().toLowerCase())).map((r) => (
        <div key={r.key} className={card} data-testid={`admin-player-${r.key.replace(/\s+/g, "-")}`}>
          <div className="flex items-center gap-3 mb-2">
            <Avatar name={r.name} photoPath={r.photo_path} size={34} />
            <div className="min-w-0">
              <p className="font-semibold text-slate-100 truncate">{r.name}</p>
              <p className="text-xs text-slate-500">{r.has_account ? "Con account" : "Senza account"}{r.hidden ? " · scheda cancellata" : ""}</p>
            </div>
          </div>
          <AdminPlayerActions name={r.name} hasPhoto={!!r.photo_path} hidden={r.hidden} onChanged={changed} slug={r.key.replace(/\s+/g, "-")} />
        </div>
      ))}
    </>
  );
};

const SECTIONS = [["iscritti", "Iscritti"], ["giocatori", "Schede giocatori"], ["partite", "Partite"], ["segnalazioni", "Segnalazioni"], ["voti", "Votazioni"], ["pagelle", "Pagelle"]];

export const AdminPanel = ({ matches, onMatchesChanged }) => {
  const [section, setSection] = useState("iscritti");
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const loadUsers = useCallback(() => api.adminUsers().then(setUsers).catch(() => toast.error("Impossibile caricare gli iscritti")), []);
  useEffect(() => { loadUsers(); }, [loadUsers]);

  return (
    <div data-testid="admin-panel">
      <div className="flex flex-wrap gap-2 mb-4">
        {SECTIONS.map(([id, label]) => (
          <button key={id} onClick={() => setSection(id)} data-testid={`admin-section-${id}`}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${section === id ? "bg-[#ccff00] text-[#090d16]" : "bg-[#121b2d] border border-[#1e2c4a] text-slate-300 hover:text-white"}`}>
            {label}
          </button>
        ))}
      </div>
      <div className="space-y-2">
        {section === "iscritti" && (
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca iscritto..." data-testid="admin-user-search" className="bg-[#121b2d] border-[#1e2c4a] text-slate-100 h-10 mb-1" />
        )}
        {section === "iscritti" && users.filter((u) => u.username_lower.includes(q.trim().toLowerCase())).map((u) => <UserRow key={u.id} u={u} reload={() => { loadUsers(); onMatchesChanged(); }} />)}
        {section === "partite" && (matches.length ? [...matches].reverse().map((m) => <MatchRow key={m.id} m={m} users={users} reload={onMatchesChanged} />)
          : <p className="text-sm text-slate-500">Nessuna partita.</p>)}
        {section === "giocatori" && <PlayersList onChanged={() => { loadUsers(); onMatchesChanged(); }} />}
        {section === "segnalazioni" && <ReportsList />}
        {section === "voti" && <VotesList />}
        {section === "pagelle" && <RatingsList />}
      </div>
    </div>
  );
};
